import { Node, mergeAttributes, type Extensions, type JSONContent } from '@tiptap/core';
import { ListItem } from '@tiptap/extension-list';
import StarterKit from '@tiptap/starter-kit';

/*
 * The rich text the portal writes: the same allowed list as the backend's
 * `app/services/rich_text.py`, which refuses anything else on save and turns
 * the stored JSON into HTML for the app. See `docs/rich-text.md` in the
 * backend repository.
 *
 * Headings at levels 2 and 3, paragraphs, bold, italic, bulleted and numbered
 * lists, links, images with a description, and a call-to-action button. No
 * quotations, code, dividing lines, line breaks, underlining or
 * strikethrough. An image and a button stand on their own at the top level of
 * the document, never inside a list, as the backend requires.
 */

/** Address schemes a link or a button may use, as the backend allows them. */
export const ALLOWED_SCHEMES = ['https:', 'mailto:', 'tel:'] as const;

/** An address a link or a button may open. */
export function allowedAddress(href: string | null | undefined): boolean {
  const value = (href ?? '').trim().toLowerCase();
  return ALLOWED_SCHEMES.some((scheme) => value.startsWith(scheme));
}

/** The words for a refused address, used by the toolbar's two address boxes. */
export const ADDRESS_RULE = 'Start the address with https://, mailto: or tel:';

/**
 * The call-to-action button: a label and an address, one block on its own.
 * In the editor it reads as a button; in the app it is the backend's
 * `<a class="cta-button">`.
 */
export const CtaButton = Node.create({
  name: 'ctaButton',
  group: 'topLevel',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      label: { default: '' },
      href: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-cta-button]',
        // A pasted button without a label or an allowed address is dropped,
        // as the backend would refuse it.
        getAttrs: (element) => {
          const label = element.textContent ?? '';
          const href = element.getAttribute('data-href') ?? '';
          return label.trim() && allowedAddress(href) ? { label, href } : false;
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    // A span, not a link: in the editor it is something to select and
    // delete, and clicking it must not leave the page. The app gets the real
    // link from the backend's renderer.
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-cta-button': '',
        'data-href': node.attrs.href as string,
        class: 'cta-button',
        title: node.attrs.href as string,
      }),
      node.attrs.label as string,
    ];
  },
});

/** Looks up the address to show a stored image by, or null when there is none. */
export type ImageAddress = (key: string) => string | null;

/**
 * An image: a storage key and its description (alternative text). The
 * document never holds an address - a signed one expires - so the editor
 * shows the image through `resolve`, which the ARC's `intro_images` and the
 * images uploaded since feed. An image whose address is not known yet shows
 * its description instead.
 */
export const ArcImage = Node.create<{ resolve: ImageAddress }>({
  name: 'image',
  group: 'topLevel',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { resolve: () => null };
  },

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') ?? '',
        renderHTML: (attrs) => ({ 'data-key': attrs.key as string }),
      },
      alt: {
        default: '',
        parseHTML: (element) => element.getAttribute('alt') ?? '',
        renderHTML: (attrs) => ({ alt: attrs.alt as string }),
      },
    };
  },

  parseHTML() {
    // Only an image this editor wrote: a pasted picture has no key.
    return [{ tag: 'img[data-key]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const src = this.options.resolve(node.attrs.key as string);
    return [
      'img',
      mergeAttributes(HTMLAttributes, { class: 'arc-image', ...(src ? { src } : {}) }),
    ];
  },
});

/**
 * The document itself: the starter kit's blocks, plus the button and the
 * image, which may stand only here.
 */
const ArcDocument = Node.create({
  name: 'doc',
  topNode: true,
  content: '(block | topLevel)+',
});

/**
 * A list item holds paragraphs and lists only, as the backend allows. The
 * starter kit's takes any block after its first paragraph, so a pasted
 * heading inside a list would be kept and the save refused.
 */
const ArcListItem = ListItem.extend({
  content: 'paragraph (paragraph | bulletList | orderedList)*',
});

/** The editor's extensions: the starter kit trimmed to the allowed list. */
export function richTextExtensions(resolve?: ImageAddress): Extensions {
  return [
    ArcDocument,
    StarterKit.configure({
      document: false,
      heading: { levels: [2, 3] },
      blockquote: false,
      code: false,
      codeBlock: false,
      hardBreak: false,
      horizontalRule: false,
      listItem: false,
      strike: false,
      underline: false,
      link: {
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        defaultProtocol: 'https',
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        isAllowedUri: (url) => allowedAddress(url),
      },
    }),
    ArcListItem,
    CtaButton,
    ...(resolve ? [ArcImage.configure({ resolve })] : []),
  ];
}

/** Characters of text in a document, as the backend counts them. */
export function textLength(doc: JSONContent | null | undefined): number {
  if (!doc) return 0;
  const own =
    (typeof doc.text === 'string' ? doc.text.length : 0) +
    (doc.type === 'ctaButton' ? String(doc.attrs?.label ?? '').length : 0) +
    (doc.type === 'image' ? String(doc.attrs?.alt ?? '').length : 0);
  return own + (doc.content ?? []).reduce((sum, child) => sum + textLength(child), 0);
}

/** True when a document has nothing in it a person would see. */
export function isEmptyDocument(doc: JSONContent | null | undefined): boolean {
  if (!doc) return true;
  return textLength(doc) === 0;
}

/** A document's words as one line, for a short preview. */
export function plainText(doc: JSONContent | null | undefined): string {
  if (!doc) return '';
  if (typeof doc.text === 'string') return doc.text;
  if (doc.type === 'ctaButton') return String(doc.attrs?.label ?? '');
  const parts = (doc.content ?? []).map(plainText).filter(Boolean);
  return parts.join(doc.type === 'doc' || doc.type?.endsWith('List') ? ' ' : '').trim();
}
